import React, { useState } from "react";
import { FieldGroup } from "../../../components";
import { Button } from "../../../components/ui";
import { useRequestContext } from "../../hooks/useRequest";
import QuotesView from "../../../order/components/Quotes/QuotesView";
import { ItemStateProvider } from "../../../order/hooks";
import { quoteActionOptions, requestTypes } from "../../constants";
import { useSendQuote, useUpdateQuoteType } from "../../../order/hooks/quotes";
import { Success } from "./Success";
import { SelectQuoteChecklist } from "./SelectQuoteChecklist";

export function SendQuote({
  step,
  idx,
  onAdvance,
  navigateTo,
  onSelectQuote,
  // Supplied by RequestSectionView from the sections that actually exist. A
  // main slot picked from Active Listings has no quote section, so "is this the
  // last order quote?" cannot be answered by counting products.
  isLastOrderQuote = false,
}) {
  const {
    getContext,
    afterSendQuote,
    toggleStep,
    setRequest,
    setRequestPreservingRelations,
    slotSetRequest,
    setLastEvent,
    pxProducts,
    refreshRequest,
    selectSlotQuote,
  } = useRequestContext();
  // Pass the step so getContext honors step.pxSellIdx /
  // step.mainSlotIdx — without this, dynamic per-slot SEND QUOTE
  // sections (multi-main: SEND QUOTE 2/2, 3/3) would all read the
  // primary's request and the rep would create/select the wrong
  // slot's quote.
  const { request } = getContext(idx, step);
  const { sendQuote, status: isSendingQuote } = useSendQuote();
  const { updateQuoteType, status: isUpdatingQuoteType } = useUpdateQuoteType();
  const [quoteId, setQuoteId] = useState(null);
  const [quoteAction, setQuoteAction] = useState(
    request.last_quote?.quote_send_type ?? quoteActionOptions[0].id
  );
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [refreshList, setRefreshList] = useState(0);
  const [isShowButton, setIsShowButton] = useState(true);

  const isOrderRequest = request.request_type === "order";
  const [type, setType] = useState(isOrderRequest ? "order" : "sell");

  const isSellRequest = request.request_type === requestTypes.SELL;
  // A SELECT SELL QUOTE section — one per part-exchange trade-in. Distinct from
  // the standalone sell flow (also a sell request, but no pxSellIdx), which
  // continues on to VERIFY SERIAL instead of the PX chain.
  const isPxSellSlot = step?.pxSellIdx != null;

  const [showSuccess, setShowSuccess] = useState(false);

  const onSelectedQuote = (quote) => {
    setSelectedQuote(quote);
    // In V2, propagate the checkmarked quote upward so CONNECT LISTING
    // (and any other section that cares) targets THIS quote instead of
    // request.last_quote.
    onSelectQuote?.(quote);

    // A trade-in's chosen quote IS the buying side of the deal, so it has to
    // reach the `quotes` map immediately: PX SUMMARY's net and the Live
    // Calculation banner are computed from it. Writing it on SELECTION (not just
    // on Save) is what makes that banner live.
    if (isPxSellSlot && quote?.id) {
      selectSlotQuote?.(request.id, quote);
    }

    if (quote.type === "sell" && !!quote.quote_send_type) {
      setQuoteAction(quote.quote_send_type);
    }
  };

  const onSendQuote = async () => {
    await sendQuote({ id: selectedQuote.id, type: quoteAction });
    afterSendQuote(quoteAction);
  };

  const onSave = async () => {
    setLastEvent("save");
    // Persist the quote-type change only when the rep actually has a
    // quote selected/checked. Previously this always did
    // updateQuoteType({ id: selectedQuote.id }) — with no selection,
    // selectedQuote is null, so `selectedQuote.id` threw and the button
    // (whether labelled "Save" or "Next") silently did nothing, leaving
    // the rep stuck on SELECT QUOTE. Save/Next must ALWAYS advance to the
    // appropriate next section; a missing/failed quote-type update is
    // non-fatal and must not block navigation.
    if (selectedQuote?.id) {
      try {
        await updateQuoteType({ id: selectedQuote.id, type: quoteAction });
        afterSendQuote(quoteAction);
      } catch (e) {
        console.error("[RC] updateQuoteType failed on SELECT QUOTE save", e);
      }
    }
    if (!isOrderRequest) {
      setShowSuccess(true);
    }
    // PX trade-in quote: walk the sell quotes, then hand over to PX SUMMARY on
    // the last one — that is where the completed deal gets reviewed. Checked
    // BEFORE the standalone-sell branch below, because a trade-in is a sell
    // request too and would otherwise be sent to VERIFY SERIAL.
    if (isPxSellSlot) {
      if (selectedQuote?.id) await selectSlotQuote?.(request.id, selectedQuote);
      const totalSells = pxProducts?.length ?? 1;
      const isLastSellQuote = step.pxSellIdx >= totalSells - 1;
      navigateTo?.(
        isLastSellQuote ? "px_summary" : `send_quote_sell_${step.pxSellIdx + 1}`
      );
      return;
    }

    // Standalone sell flow leaves the quote block via VERIFY_SERIAL — the
    // SELECT QUOTE → VERIFY_SERIAL → AGREEMENTS → SELL_PAYMENTS →
    // PURCHASE_ORDER → PUBLISH_LISTING chain. Order flow keeps the
    // existing per-slot rules below.
    if (isSellRequest && navigateTo) {
      navigateTo("verify_serial");
      return;
    }

    // Order-flow advancement for the SELECT QUOTE block (v2):
    //   - SELECT QUOTE 1/m → next quote section (handled by
    //     RequestSectionView's onAdvance → captured `next`)
    //   - SELECT QUOTE n/m (last) → the FIRST sell quote when this is a
    //     part-exchange, so the trade-ins get quoted before the deal is
    //     reviewed (PX SUMMARY then closes the chain); otherwise straight to
    //     INTERNAL DETAILS & COSTS, which now precedes PAYMENTS so the serial
    //     is recorded before the invoice that quotes it is generated. A deal
    //     with no linked listing has no such section, and resolveNavTarget
    //     falls the hand-over through to PAYMENTS.
    // Last-quote detection comes from the RENDERED section list (the
    // isLastOrderQuote prop), not from a product count: a main slot picked from
    // Active Listings has no quote section at all, so counting products would
    // point at a section that is not there and the hand-over never fired.
    const isLastQuote = isLastOrderQuote;

    if (isLastQuote && navigateTo) {
      navigateTo(pxProducts?.length ? "send_quote_sell_0" : "internal_details");
      return;
    }
    onAdvance?.();
  };

  const selectQuote = (quoteId) => {
    setQuoteId(quoteId);
  };

  const applySavedQuote = (quote) => {
    // Route the updated Request back into the correct slot. For the
    // primary main slot (step.mainSlotIdx == null) this is the legacy
    // setRequest behaviour. For additional main slots / PX slots,
    // slotSetRequest writes into additionalMainRequests[i] or
    // pxRequests[i] without clobbering other slots.
    if (quote?.request) {
      if (slotSetRequest) {
        slotSetRequest(step, quote.request);
      } else {
        // Use the relations-preserving setter so the smart card retains
        // the product/client/last_quote hydration if the quote-save
        // response omits any of them.
        setRequestPreservingRelations(quote.request);
      }
    } else {
      // Action endpoints (send-quote, MOVE_NEGOTIATION, accepted-by-client,
      // create/update-listing) return Quote::find() with no eager-loaded
      // request, so quote.request is absent. Re-fetch the request to pick
      // up the new status; without this the status pill / smart card stay
      // stale until the page is reloaded.
      refreshRequest?.();
    }
    setRefreshList((n) => n + 1);
  };

  const onQuoteSaved = (quote) => {
    applySavedQuote(quote);
    if (quote?.id) setQuoteId(quote.id);
  };

  // An ACTION taken from a quote's own ROW. Two differences from saving inside the editor:
  //
  //  - it must NOT set `quoteId`, which is what opens the editor modal — the rep is looking at
  //    the list, and popping the editor over it is not what they asked for;
  //  - the "Before you continue" checklist has to move NOW. It reads `selectedQuote` first and
  //    only falls back to `request.last_quote`, so refreshing the request is a round trip that
  //    leaves the tick grey in the meantime. Feeding the quote the server just returned back
  //    through `onSelectedQuote` flips it immediately, and keeps the propagated selection
  //    (CONNECT LISTING, PX SUMMARY, the Live Calculation banner) on the same object.
  const onRowQuoteSaved = (quote) => {
    applySavedQuote(quote);
    if (quote?.id && (!selectedQuote || selectedQuote.id === quote.id)) {
      onSelectedQuote(quote);
    }
  };

  const toggleShowButton = () => {
    toggleStep(idx);
  };

  return (
    <ItemStateProvider item={request} request={request} defaultSelection>
      <QuotesView
        item={request}
        quoteId={quoteId}
        onSelectedQuote={onSelectedQuote}
        selectQuote={selectQuote}
        onQuoteSaved={onQuoteSaved}
        onRowQuoteSaved={onRowQuoteSaved}
        refreshList={refreshList}
        toggleShowButton={toggleShowButton}
        step={step}
        type={type}
        setType={setType}
        // V2 request flow only (step present): the "Before you continue"
        // checklist. Warn-but-allow — Save/Next below stay enabled either way.
        beforeList={
          step ? (
            <SelectQuoteChecklist
              request={request}
              selectedQuote={selectedQuote}
              isPxSellSlot={isPxSellSlot}
            />
          ) : null
        }
      />
      {step.open && (
        <div className="quote-list-footer">
          <Button variant="primary" size="lg" onClick={onSave} data-btn="quote-select-save">
            {isOrderRequest && !selectedQuote?.listing_id ? "Save" : "Next"}
          </Button>
        </div>
      )}
    </ItemStateProvider>
  );
}
